import React, { createContext, useContext, FC, useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { AuthApi, HttpClient, UserApi } from '../../api';
import { noop } from '../../utils';
import { useLocation, useNavigate } from 'react-router-dom';

export interface AuthContextValue {
  isAuthorized: boolean | undefined;
  signIn(token: string, opts?: { navigate?: boolean }): Promise<void>;
  signOut(): void;
}

export const AuthContext = createContext<AuthContextValue>({
  isAuthorized: undefined,
  signIn: noop as any,
  signOut: noop,
});

export const useAuthContext = (): AuthContextValue => useContext(AuthContext);

export const AuthContextProvider: FC = ({ children }) => {
  const { refetch, remove } = useQuery('user', UserApi.fetch, { enabled: false });
  const [authorized, setAuthorized] = useState<boolean | undefined>(undefined);

  const navigate = useNavigate();
  const location = useLocation();

  const getNextTarget = () => {
    const stateNext = (location.state as any)?.next as string | undefined;
    if (stateNext) return stateNext;

    const url = new URL(window.location.href);
    const qNext = url.searchParams.get('next');
    if (qNext) return qNext;

    // jeśli wchodzimy bezpośrednio na URL, NIE nadpisuj
    return location.pathname + location.search || '/profile';
  };

  const signOut = useCallback(async () => {
    try { await AuthApi.logout().catch(() => {}); } finally {
      localStorage.removeItem('token');
      HttpClient.clearToken();
      setAuthorized(false);
    }
  }, []);

  const signIn = useCallback(
    async (token: string, opts?: { navigate?: boolean }) => {
      HttpClient.setToken(token);
      remove();
      HttpClient.enterBootGrace(2000);

      const wait = (ms: number) => new Promise(res => setTimeout(res, ms));
      const maxAttempts = 4;
      let ok = false;

      for (let i = 0; i < maxAttempts; i++) {
        const { isError, data } = await refetch();
        if (!isError && data) { ok = true; break; }
        await wait(200 * Math.pow(2, i));
      }

      if (ok) {
        localStorage.setItem('token', token);
        setAuthorized(true);

        // nawiguj TYLKO jeśli to logowanie z formularza
        if (opts?.navigate) {
          const target = getNextTarget();
          navigate(target, { replace: true, state: {} });
        }
      } else {
        localStorage.removeItem('token');
        HttpClient.clearToken();
        setAuthorized(false);
        throw new Error('Login succeeded but /self did not load in time');
      }
    },
    [refetch, remove, navigate, location],
  );

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      HttpClient.enterBootGrace(2000);
      signIn(token, { navigate: false }).catch(() => setAuthorized(false));
    } else {
      setAuthorized(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthorized: authorized, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};